import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { TeamNews } from '@/components/profile/TeamNews';
import { Camera } from 'lucide-react';
import AudioPlayer from '@/components/audio/audio-player';
import { useProfile } from '@/lib/providers';
import { normalizeImageUrl } from '@/lib/utils';
import imageCompression from 'browser-image-compression';
import { AvatarCropDialog } from '@/components/profile/avatar-crop-dialog';
import { Whiteboard } from '@/components/team/whiteboard';
import { TeamChat } from '@/components/team/TeamChat';
import { TeamMeditationCalendar } from '@/components/team/TeamMeditationCalendar';
import { MeditationCountdown } from '@/components/team/MeditationCountdown';
import { PlanTeamMeditationDialog } from '@/components/team/PlanTeamMeditationDialog';
import { TeamMeditationSession } from '@/components/team/TeamMeditationSession';
import { SectionBackground } from '@/components/ui/section-background';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getIdenticon, getMostRecentMondayUTC } from '@/lib/utils';
import Loading from '@/components/loading';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { TeamFull } from '@/types/custom';
import { TypesBarChart } from '@/components/charts/types-bar-chart';
import { useContentPlays } from '@/lib/hooks/use-content-plays';
import { useTeamMeditations, TeamMeditation } from '@/lib/hooks/use-team-meditations';
import { Database } from '@/types/supabase';

export default function TeamPage() {
  const { profile, loadingProfile, refreshProfile } = useProfile();
  const [memberCount, setMemberCount] = useState(0);
  const [orgUserIds, setOrgUserIds] = useState<string[]>([]);
  const [teamUserIds, setTeamUserIds] = useState<string[]>([]);
  const [pastMeditations, setPastMeditations] = useState<TeamMeditation[]>([]);
  const [uploading, setUploading] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [teamAvatarUrl, setTeamAvatarUrl] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'team-spirit');
  const [sessionDismissed, setSessionDismissed] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const tabsRef = useRef<HTMLDivElement>(null);

  // Get the current team based on active_team_idx
  const team: TeamFull | undefined = profile?.teams.at(profile.active_team_idx);
  const isAdmin = profile?.role === 'admin';

  useEffect(() => {
    if (team?.avatar_url) setTeamAvatarUrl(team.avatar_url);
  }, [team?.avatar_url]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropFile(file);
    setShowCropDialog(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCroppedUpload = async (blob: Blob) => {
    if (!team) return;
    setShowCropDialog(false);
    setUploading(true);
    try {
      const compressed = await imageCompression(
        new File([blob], 'avatar.webp', { type: 'image/webp' }),
        { maxSizeMB: 0.2, maxWidthOrHeight: 400, useWebWorker: true },
      );
      const filePath = `teams/${team.id}/avatar.webp`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, compressed, { upsert: true, contentType: 'image/webp' });
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
      const avatarUrl = `${publicUrl}?t=${Date.now()}`;

      const { error: updateError } = await supabase
        .from('team')
        .update({ avatar_url: avatarUrl })
        .eq('id', team.id);
      if (updateError) throw updateError;

      setTeamAvatarUrl(avatarUrl);
      refreshProfile();
    } catch (err) {
      console.error('Team avatar upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const {
    meditations,
    hasUpcoming,
    nextMeditation,
    activeMeditation,
    activeMeditations,
    deleteMeditation,
    refetch: refetchMeditations,
  } = useTeamMeditations(team?.id);

  // Pick a session to render when none is open, but never swap mid-session.
  useEffect(() => {
    if (currentSessionId) {
      if (!activeMeditations.some((m) => m.id === currentSessionId)) {
        setCurrentSessionId(null);
      }
      return;
    }
    if (!sessionDismissed && activeMeditations.length > 0) {
      setCurrentSessionId(activeMeditations[0].id);
    }
  }, [activeMeditations, currentSessionId, sessionDismissed]);

  const currentSession = currentSessionId
    ? activeMeditations.find((m) => m.id === currentSessionId) ?? null
    : null;

  const mostRecentMonday = useMemo(() => getMostRecentMondayUTC(), []);

  useEffect(() => {
    if (team) {
      // Fetch org-wide user IDs
      supabase
        .from('team_user_link')
        .select('user_id, team!inner(organization_id)')
        .eq('team.organization_id', team.organization.id)
        .then(({ data }) => {
          if (data) {
            const ids = data.map((row: any) => row.user_id);
            setOrgUserIds(ids);
            setMemberCount(ids.length);
          }
        });

      // Fetch team member IDs
      supabase
        .from('team_user_link')
        .select('user_id')
        .eq('team_id', team.id)
        .then(({ data }) => {
          if (data) setTeamUserIds(data.map((row: any) => row.user_id));
        });

      // Fetch past team meditations
      supabase
        .from('team_meditations')
        .select('*')
        .eq('team_id', team.id)
        .order('scheduled_at', { ascending: false })
        .then(({ data }) => {
          if (data) {
            const now = new Date();
            const past = data.filter((m) => {
              const end = new Date(m.scheduled_at);
              end.setMinutes(end.getMinutes() + (m.duration_minutes ?? 30));
              return end <= now;
            });
            setPastMeditations(past);
          }
        });
    }
  }, [team?.id, team?.organization?.id]);

  const { contentPlays } = useContentPlays(
    teamUserIds.length > 0
      ? { userIds: teamUserIds, startTime: mostRecentMonday }
      : { disabled: true },
  );

  const switchToMeditationTab = () => {
    setActiveTab('team-meditatie');
    tabsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loadingProfile) {
    return <Loading />;
  }

  if (!team) {
    return (
      <Alert className="w-full bg-black/60 backdrop-blur-md border border-white/10 shadow-lg flex items-center justify-center">
        <AlertDescription className="text-white text-center">
          <AlertTitle className="text-white">Team niet gevonden.</AlertTitle>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="flex flex-col items-center gap-8">
        <div className="relative group">
          <Avatar className="w-24 h-24">
            <AvatarImage src={teamAvatarUrl || getIdenticon(team)} />
            <AvatarFallback>TM</AvatarFallback>
          </Avatar>
          {isAdmin && (
            <>
              <button
                type="button"
                className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <Camera className="h-6 w-6 text-white" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />
            </>
          )}
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50">
              <div className="h-6 w-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        <AvatarCropDialog
          file={cropFile}
          open={showCropDialog}
          onClose={() => setShowCropDialog(false)}
          onCrop={handleCroppedUpload}
        />
        <div className="text-center space-y-2">
          <p className="text-sm text-muted-foreground mb-2">
            Organisatie: {team.organization.name}
          </p>
          <div className="pt-2 border-t">
            <h3 className="text-lg font-semibold">{team.name}</h3>
          </div>
        </div>

        {/* Active meditation banner — visible across all tabs when session is not open */}
        {activeMeditation && !currentSession && (
          <div className="w-full max-w-4xl mx-auto px-4 sm:px-0">
            <MeditationCountdown
              meditation={null}
              activeMeditation={activeMeditation}
              onSwitchToTab={() => {
                setSessionDismissed(false);
                setCurrentSessionId(activeMeditation.id);
                setActiveTab('team-meditatie');
              }}
            />
          </div>
        )}

        <div ref={tabsRef} className="w-full max-w-4xl mx-auto px-4 sm:px-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full grid grid-cols-2 sm:grid-cols-4 h-auto">
              <TabsTrigger value="team-nieuws" className="text-xs sm:text-sm py-2">
                Team Nieuws
              </TabsTrigger>
              <TabsTrigger value="team-meditatie" className="text-xs sm:text-sm py-2 relative">
                Team Activiteiten
                {hasUpcoming && (
                  <span className={`absolute top-1 right-1 h-2.5 w-2.5 rounded-full ${activeMeditation ? 'bg-green-500 animate-pulse' : 'bg-green-500'}`} />
                )}
              </TabsTrigger>
              <TabsTrigger value="team-spirit" className="text-xs sm:text-sm py-2">
                Team Spirit
              </TabsTrigger>
              <TabsTrigger value="team-beluisterd" className="text-xs sm:text-sm py-2">
                Team Beluisterd
              </TabsTrigger>
            </TabsList>

            <TabsContent value="team-nieuws" className="mt-4">
              <TeamNews />
            </TabsContent>

            <TabsContent value="team-meditatie" className="mt-4">
              <div className="space-y-6">
                {/* Active meditation session (fullscreen) */}
                {currentSession && (
                  <TeamMeditationSession
                    key={currentSession.id}
                    meditation={currentSession}
                    onEnded={() => {
                      setSessionDismissed(true);
                      setCurrentSessionId(null);
                      refetchMeditations();
                    }}
                  />
                )}

                <SectionBackground className="w-full mx-auto bg-white p-4">
                  <h3 className="text-lg font-medium">Team Activiteiten</h3>
                  <p className="text-muted-foreground mt-2">
                    Hier kun je een activiteit plannen met je team en zien welke activiteiten er gepland zijn (en meteen deelnemen). Dit kunnen alle soorten Meditaties zijn, Ademhalingsoefeningen, Lichaamsbewustzijnsoefeningen of Dance Breaks.
                  </p>
                  <div className="mt-4">
                    <PlanTeamMeditationDialog
                      teamId={team.id}
                      onCreated={refetchMeditations}
                    />
                  </div>
                </SectionBackground>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full mx-auto">
                  <TeamMeditationCalendar
                    meditations={meditations}
                    activeMeditationId={activeMeditation?.id}
                    activeMeditationIds={activeMeditations.map((m) => m.id)}
                    onDelete={deleteMeditation}
                    onOpenActive={(id) => {
                      setSessionDismissed(false);
                      setCurrentSessionId(id);
                    }}
                    title="Team Activiteiten Kalender"
                  />
                  <TeamChat meditationId={activeMeditation?.id ?? nextMeditation?.id} />
                </div>

                {/* Countdown to next meditation (only when no active session) */}
                {!activeMeditation && (
                  <div className="mt-6 w-full mx-auto">
                    <MeditationCountdown
                      meditation={nextMeditation}
                      activeMeditation={null}
                    />
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="team-spirit" className="mt-4">
              <Whiteboard />
            </TabsContent>

            <TabsContent value="team-beluisterd" className="mt-4">
              <div className="space-y-6">
                <SectionBackground
                  className="p-6 w-full bg-transparent"
                  variant="secondary"
                >
                  <h3 className="text-lg font-medium mb-4">
                    Team Beluisterd (deze week)
                  </h3>
                  <TypesBarChart contentPlays={contentPlays} />
                </SectionBackground>

                <SectionBackground
                  className="p-6 w-full bg-transparent"
                  variant="secondary"
                >
                  <h3 className="text-lg font-medium mb-4">
                    Team Meditaties
                  </h3>
                  {pastMeditations.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Er zijn nog geen afgeronde team meditaties.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {pastMeditations.map((m) => {
                        const date = new Date(m.scheduled_at);
                        return (
                          <div
                            key={m.id}
                            className="flex items-center justify-between p-3 rounded-lg border bg-card"
                          >
                            <div className="flex items-center gap-3">
                              {m.image_url && (
                                <img
                                  src={normalizeImageUrl(m.image_url)}
                                  alt={m.meditation_title}
                                  className="w-10 h-10 rounded object-cover"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              )}
                              <div>
                                <p className="font-medium text-sm">{m.meditation_title}</p>
                                <p className="text-xs text-muted-foreground">
                                  {date.toLocaleDateString('nl-NL', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                  })}
                                  {' om '}
                                  {date.toLocaleTimeString('nl-NL', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </p>
                              </div>
                            </div>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {m.duration_minutes} min
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </SectionBackground>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
